"use client";

import { Check, Copy, Loader2, Quote } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  type BibStrings,
  useBibStrings,
} from "@/components/citation-tool/tool-strings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trackInTextCopied } from "@/lib/analytics";
import type { SavedCitation } from "@/lib/bibliography";
import type { CitationLocatorLabel } from "@/lib/citation";
import { writeToClipboardOrThrow } from "@/lib/clipboard";

/**
 * The locators worth putting in a dropdown, in the order people reach for them.
 * The full CSL list is longer; these cover essentially every student citation, and a
 * 17-item menu for a one-line action is worse than a 6-item one.
 */
const COMMON_LABELS: CitationLocatorLabel[] = [
  "page",
  "chapter",
  "section",
  "paragraph",
  "line",
  "verse",
];

/** Locator labels are translated, so they come from the dictionary, not a constant. */
function labelText(bib: BibStrings): Record<string, string> {
  return {
    page: bib.locatorPage,
    chapter: bib.locatorChapter,
    section: bib.locatorSection,
    paragraph: bib.locatorParagraph,
    line: bib.locatorLine,
    verse: bib.locatorVerse,
  };
}

const PLACEHOLDER: Record<string, string> = {
  page: "88 or 88–90",
  chapter: "4",
  section: "2.1",
  paragraph: "7",
  line: "12",
  verse: "3",
};

interface InTextCitationPopoverProps {
  citation: SavedCitation;
  styleName: string;
}

/**
 * Build and copy the in-text citation for one saved source, optionally pinned to a
 * page, chapter, or other locator.
 *
 * The generator shows the in-text form for the citation you just made, then loses it
 * the moment the entry is saved — so mid-draft, "what's the parenthetical for this
 * again?" meant re-citing the source from scratch. The saved entry is all citeproc
 * needs. The locator is what every style requires alongside a direct quotation, and
 * it is rendered BY the style: APA writes "p. 88", MLA writes it its own way, and a
 * note style folds it into the footnote.
 */
export function InTextCitationPopover({
  citation,
  styleName,
}: InTextCitationPopoverProps) {
  const bib = useBibStrings();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState<CitationLocatorLabel>("page");
  const [value, setValue] = useState("");
  const [result, setResult] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function build() {
    setLoading(true);
    setResult(null);
    try {
      const trimmed = value.trim();
      const res = await fetch("/api/cite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          entry: citation.entry,
          style: citation.style,
          inText: true,
          locator: trimmed ? { label, value: trimmed } : undefined,
        }),
      });
      if (!res.ok) {
        throw new Error(bib.inTextFailed);
      }
      const data = (await res.json()) as { inText?: string };
      const inText = data.inText?.trim();
      if (!inText) {
        toast.error(
          `${styleName} does not produce a separate in-text citation for this source.`
        );
        return;
      }
      setResult(inText);
      try {
        await writeToClipboardOrThrow(inText);
        trackInTextCopied(citation.style, Boolean(trimmed));
        toast.success(`Copied ${inText}`);
      } catch {
        // Clipboard can be blocked; the result is still shown to copy by hand.
        toast.message(bib.inTextSelect);
      }
    } catch {
      toast.error(bib.inTextFailed);
    } finally {
      setLoading(false);
    }
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      setResult(null);
    }
  }

  return (
    <Popover onOpenChange={handleOpenChange} open={open}>
      <PopoverTrigger asChild>
        <Button size="icon-xs" title={bib.inTextCopy} variant="ghost">
          <Quote className="size-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-3 p-3">
        <div>
          <p className="font-medium text-foreground text-sm">
            {bib.inTextHeading}
          </p>
          <p className="mt-1 text-muted-foreground text-xs leading-relaxed">
            {bib.inTextLocatorHint}
          </p>
        </div>

        <div className="grid gap-2 sm:grid-cols-[130px_minmax(0,1fr)]">
          <Select
            onValueChange={(next) => setLabel(next as CitationLocatorLabel)}
            value={label}
          >
            <SelectTrigger
              aria-label={bib.locatorType}
              className="h-9 w-full"
              size="sm"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COMMON_LABELS.map((option) => (
                <SelectItem key={option} value={option}>
                  {labelText(bib)[option] ?? option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            aria-label={bib.locator}
            className="h-9 flex-1"
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                build();
              }
            }}
            placeholder={PLACEHOLDER[label] ?? ""}
            value={value}
          />
        </div>

        <Button className="w-full" disabled={loading} onClick={build} size="sm">
          {loading ? (
            <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              Building…
            </>
          ) : (
            <>
              <Copy className="mr-2 size-4" />
              {bib.inTextCopyButton}
            </>
          )}
        </Button>

        {result ? (
          <output className="block rounded-md border border-border bg-muted/40 px-3 py-2">
            <span className="flex items-start gap-2">
              <Check
                aria-hidden="true"
                className="mt-0.5 size-3.5 shrink-0 text-muted-foreground"
              />
              <span className="wrap-break-word font-mono text-foreground text-sm">
                {result}
              </span>
            </span>
          </output>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
